// Which of the three React trees a page load mounts (CU-86bbz617u).
//
// The app ships as ONE bundle served on several hostnames (see
// cdk/lib/frontend-stack.ts). Branch selection used to be path-only
// (/cockpit, /seller); vdr.bidequity.ai adds a hostname whose ENTIRE
// surface is the external seller portal, so the host decides before the
// path does; portfolio.bidequity.ai does the same for the cockpit. That also
// closes the caveat noted in the infra code: on those hostnames the internal
// tree is unreachable, not merely auth-gated.
//
// Evaluated once at module load in App.tsx — the trees never share
// providers, so switching between them is always a hard navigation.

export type AppBranch = 'seller' | 'cockpit' | 'internal';

/** Hostnames whose whole surface is the external seller portal. */
export const SELLER_PORTAL_HOSTS: ReadonlyArray<string> = ['vdr.bidequity.ai'];

/**
 * Hostnames whose whole surface is the PortCo-facing Buy & Build Cockpit.
 *
 * Same rule as SELLER_PORTAL_HOSTS: the host decides before the path does, so
 * `https://portfolio.bidequity.ai/` mounts the cockpit tree instead of the
 * internal app's sign-in screen. The cockpit tree's own catch-all routes
 * (pages/Cockpit/CockpitApp.tsx) then move the URL to `/cockpit` when signed
 * in, or `/cockpit/sign-in` when not, so the address bar matches what renders.
 *
 * This is routing, NOT access control. One bundle is served on every hostname
 * from one S3 bucket (cdk/lib/frontend-stack.ts), so a determined user can
 * still request any path on any host. The real gates stay where they are:
 * require_cockpit_user / CockpitProvisionGate and the section policy.
 */
export const COCKPIT_HOSTS: ReadonlyArray<string> = ['portfolio.bidequity.ai'];

export function resolveAppBranch(hostname: string, pathname: string): AppBranch {
  if (SELLER_PORTAL_HOSTS.includes(hostname)) return 'seller';
  if (COCKPIT_HOSTS.includes(hostname)) return 'cockpit';
  if (pathname.startsWith('/cockpit')) return 'cockpit';
  if (pathname.startsWith('/seller')) return 'seller';
  return 'internal';
}
